import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, MapPin, ArrowRight, Plus, Minus } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import Seo from "@/components/Seo";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ORG_TYPES = [
  "Veteran Service Organization",
  "Healthcare / Mental Health Provider",
  "Workforce Agency",
  "College / University",
  "Public Agency (Federal/State/Local)",
  "Nonprofit / Community Organization",
  "Faith-based Organization",
  "LGBTQ+ Affirming Organization",
  "Philanthropic Funder",
  "Corporate / Employer",
  "Other",
];

const initialForm = {
  name: "", organization: "", title: "", email: "", phone: "",
  organization_type: "", pilot_interest: false, message: "",
};

export default function Contact() {
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);

  const update = (key) => (e) => {
    const v = e && e.target ? e.target.value : e;
    setForm((f) => ({ ...f, [key]: v }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in name, email, and a message.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/contact`, form);
      if (res.data?.ok) {
        toast.success("Message received — we'll be in touch shortly.");
        setForm(initialForm);
      } else {
        toast.error("Something went wrong. Please try again.");
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || "Unable to submit. Please try again.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <Seo
        title="Contact WHO — Request a Demo or Join the Network"
        description="Public agencies, nonprofits, healthcare systems, workforce organizations, universities, veteran service orgs, funders, and corporate partners — connect with WHO to build a warmer handoff system."
        path="/contact"
      />
      <PageHeader
        eyebrow="Contact"
        title="Let's put a WHO Guide in your community."
        lede="If your organization serves veterans, transitioning service members, military families, or community members navigating complex systems — WHO would like to hear from you. WHO Guides&trade; are the agentic AI front door — a real Guide orchestrating nine specialist agents; your team is the trusted partner on the other side of the warm handoff."
      />

      <section className="py-20 lg:py-24 bg-white border-t border-borderSubtle" data-testid="contact-form-section">
        <div className="max-w-[1240px] mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <p className="eyebrow text-gold">Get in touch</p>
            <h2 className="mt-4 font-display font-black text-navy-900 tracking-tight text-3xl lg:text-4xl leading-[1.1]">
              Every warm handoff starts with a conversation.
            </h2>
            <p className="mt-5 text-lg text-slateSoft leading-relaxed">
              Public agencies, nonprofits, healthcare systems, workforce organizations, colleges,
              veteran service organizations, funders, and corporate partners — we&apos;d love to hear
              from you.
            </p>
            <div className="mt-10 space-y-5">
              <ContactMeta icon={<Mail className="w-5 h-5" />} label="Email" value="marlon@warmhandoffhq.com" testId="contact-meta-email" />
              <ContactMeta icon={<MapPin className="w-5 h-5" />} label="Based in" value="Pittsburgh, Pennsylvania" testId="contact-meta-location" />
            </div>

            <div className="mt-10 p-6 bg-cream border border-borderSubtle rounded-lg">
              <p className="eyebrow text-navy-800">Three ways to engage</p>
              <ul className="mt-4 space-y-2 text-navy-900 font-medium">
                <li className="flex items-center gap-2"><ArrowRight className="w-4 h-4 text-gold" /> Request a Demo</li>
                <li className="flex items-center gap-2"><ArrowRight className="w-4 h-4 text-gold" /> Become a Pilot Partner</li>
                <li className="flex items-center gap-2"><ArrowRight className="w-4 h-4 text-gold" /> Join the WHO Network</li>
              </ul>
            </div>
          </div>

          <form
            onSubmit={handleSubmit}
            data-testid="contact-form"
            className="lg:col-span-7 bg-white border border-borderSubtle rounded-lg p-8 md:p-10 space-y-5"
          >
            <div className="grid md:grid-cols-2 gap-5">
              <Field label="Name *" htmlFor="name">
                <Input id="name" data-testid="contact-name-input" value={form.name} onChange={update("name")} required placeholder="Jane Doe" />
              </Field>
              <Field label="Organization" htmlFor="organization">
                <Input id="organization" data-testid="contact-organization-input" value={form.organization} onChange={update("organization")} placeholder="Your organization" />
              </Field>
              <Field label="Title" htmlFor="title">
                <Input id="title" data-testid="contact-title-input" value={form.title} onChange={update("title")} placeholder="Director of Programs" />
              </Field>
              <Field label="Email *" htmlFor="email">
                <Input id="email" type="email" data-testid="contact-email-input" value={form.email} onChange={update("email")} required placeholder="you@organization.org" />
              </Field>
              <Field label="Phone" htmlFor="phone">
                <Input id="phone" data-testid="contact-phone-input" value={form.phone} onChange={update("phone")} placeholder="(555) 555-5555" />
              </Field>
        <Field label="Organization Type" htmlFor="organization_type">
                <Select value={form.organization_type} onValueChange={update("organization_type")}>
                  <SelectTrigger id="organization_type" data-testid="contact-org-type-trigger">
                    <SelectValue placeholder="Select an option" />
                  </SelectTrigger>
                  <SelectContent data-testid="contact-org-type-content">
                    {ORG_TYPES.map((t) => (
                      <SelectItem key={t} value={t} data-testid={`org-type-option-${t.toLowerCase().replace(/\W+/g, "-")}`}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
            </div>

            <Field label="Message *" htmlFor="message">
              <Textarea
                id="message"
                data-testid="contact-message-input"
                value={form.message}
                onChange={update("message")}
                required
                rows={5}
                placeholder="Tell us about your organization and how you'd like to work with WHO."
              />
            </Field>

            <div className="flex items-start gap-3 pt-1">
              <Checkbox
                id="pilot_interest"
                data-testid="contact-pilot-checkbox"
                checked={form.pilot_interest}
                onCheckedChange={(v) => setForm((f) => ({ ...f, pilot_interest: !!v }))}
              />
              <Label htmlFor="pilot_interest" className="text-navy-900 leading-relaxed cursor-pointer">
                I&apos;m interested in becoming a pilot partner (Pittsburgh / Pennsylvania).
              </Label>
            </div>

            <div className="pt-2">
              <button
                type="submit"
                disabled={submitting}
                data-testid="contact-submit-btn"
                className="inline-flex items-center gap-2 bg-gold hover:bg-gold-deep disabled:opacity-60 disabled:cursor-not-allowed text-navy font-semibold px-7 py-3.5 rounded-md transition-colors"
              >
                {submitting ? "Sending…" : "Send Message"} <ArrowRight className="w-4 h-4" strokeWidth={2.5} />
              </button>
              <p className="mt-4 text-sm text-slateSoft">
                By submitting, you agree to be contacted by WHO regarding your inquiry. We don&apos;t share
                information without consent.
              </p>
              <p className="mt-3 text-sm text-navy font-medium" data-testid="response-time-reassurance">
                <span className="inline-block w-2 h-2 rounded-full bg-gold mr-2 align-middle" />
                We respond to every inquiry within 2 business days.
              </p>
            </div>
          </form>
        </div>
      </section>

      <section className="py-24 lg:py-32 bg-navy-900 text-white" data-testid="contact-closing">
        <div className="max-w-[1240px] mx-auto px-6 lg:px-10 text-center">
          <p className="eyebrow text-gold">Closing</p>
          <p className="mt-4 font-display font-black text-4xl md:text-5xl lg:text-6xl leading-[1.02]">
            If not you, then <span className="text-gold-light">WHO</span>.
          </p>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 lg:py-24 bg-cream border-t border-borderSubtle" data-testid="contact-faq-section">
        <div className="max-w-[1240px] mx-auto px-6 lg:px-10">
          <div className="max-w-3xl mb-12">
            <p className="eyebrow text-gold-deep">Frequently Asked</p>
            <h2 className="mt-4 font-display font-black text-navy tracking-tight text-3xl md:text-4xl lg:text-5xl leading-[1.05]">
              Questions we hear from partners.
            </h2>
          </div>
          <div className="max-w-3xl divide-y divide-borderSubtle">
            {[
              {
                q: "What kind of organization can partner with WHO?",
                a: "Veteran service organizations, healthcare systems, workforce boards, colleges and universities, public agencies at every level, nonprofits, faith and community-based organizations, philanthropic funders, and corporate veteran programs — anyone who serves the transitioning veteran, family, or community member.",
              },
              {
                q: "Is WHO available now?",
                a: "WHO is preparing for a Pittsburgh-area pilot with Pennsylvania partners. Early partners help shape intake, handoff, and outcome practices that will scale to other communities. Contact us to be considered as a pilot partner.",
              },
              {
                q: "How does WHO handle data privacy and consent?",
                a: "Consent-first data sharing, role-based access, and encrypted communication protect the people we serve. Information is only shared between the specific partner organizations required to complete a warm handoff — and only with the person's consent.",
              },
              {
                q: "Do you replace our existing case management system?",
                a: "No. WHO is built to work alongside existing case management, EHR, workforce, and CRM systems — not replace them. The Relationship Operating System lives between your systems: WHO Guides handle the voice-native intake and the warm handoff, then hand structured, consented context into whatever your team already uses.",
              },
              {
                q: "What are WHO Guides? Is a real person involved, or is it just AI?",
                a: "WHO Guides™ are agentic AI care coordinators — backed by real people. A real WHO Guide orchestrates nine specialist AI agents (Healthcare, Benefits, Housing, Employment, Education, Transportation, Family, Community, Financial), each responsible for its own domain and follow-through. Together they coordinate services, track referrals, confirm completion, and measure outcomes. Agents do the coordination. Humans stay accountable for the human moment.",
              },
              {
                q: "How much does WHO cost partners?",
                a: "Pilot partners work directly with our team on a case-by-case basis. Long-term pricing will be structured for public-sector, healthcare, workforce, university, and nonprofit budgets. Talk to us for a tailored conversation.",
              },
              {
                q: "How long until we hear back?",
                a: "We respond to every inquiry within 2 business days. Serious pilot conversations typically move to a scheduled call within the following week.",
              },
            ].map((item, i) => (
              <FaqItem key={i} q={item.q} a={item.a} index={i} />
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

function FaqItem({ q, a, index }) {
  const [open, setOpen] = useState(index === 0);
  return (
    <div className="py-2" data-testid={`faq-item-${index}`}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-start justify-between gap-6 py-5 text-left group focus:outline-none focus-visible:ring-2 focus-visible:ring-gold rounded-md"
        aria-expanded={open}
      >
        <span className="font-display font-bold text-navy text-lg lg:text-xl group-hover:text-gold-deep transition-colors">
          {q}
        </span>
        <span className="mt-1 shrink-0 inline-flex items-center justify-center w-8 h-8 rounded-full border border-gold text-gold-deep">
          {open ? <Minus className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
        </span>
      </button>
      <div
        className={`grid transition-[grid-template-rows] duration-300 ease-out ${open ? "grid-rows-[1fr]" : "grid-rows-[0fr]"}`}
      >
        <div className="overflow-hidden">
          <p className="pb-6 pr-14 text-slateSoft leading-relaxed">{a}</p>
        </div>
      </div>
    </div>
  );
}

function Field({ label, htmlFor, children }) {
  return (
    <div className="flex flex-col gap-2">
      <Label htmlFor={htmlFor} className="text-navy-900 text-sm font-semibold">{label}</Label>
      {children}
    </div>
  );
}

function ContactMeta({ icon, label, value, testId }) {
  return (
    <div className="flex items-start gap-4" data-testid={testId}>
      <span className="mt-0.5 inline-flex w-10 h-10 items-center justify-center rounded-md bg-navy-50 text-navy-800 border border-borderSubtle">
        {icon}
      </span>
      <div>
        <p className="eyebrow text-slateSoft">{label}</p>
        <p className="mt-1 text-navy-900 font-semibold">{value}</p>
      </div>
    </div>
  );
}
